import { create } from "zustand";

import type { BreadcrumbLeafActions } from "./GlobalBreadcrumb";

/**
 * What the current page lets you do to its leaf crumb.
 *
 * The same escape hatch as `globalBreadcrumbStore`, for the same reason its
 * comment gives: the bar must not call a hook that belongs to a page. Thread
 * actions need `useThreadActions`, which needs a QueryClient — and the bar is
 * mounted on surfaces that have neither, including the error boundary, which
 * is why `AppTopBarBreadcrumb` was split out in the first place. Calling it up
 * there took the whole bar down with `No QueryClient set`.
 *
 * So the thread route publishes its actions while it is mounted, and the bar
 * renders whatever it is handed. A route that publishes nothing gets a leaf
 * that is only a name — which is every route but one today.
 *
 * Keyed by pathname like the label store, so actions belonging to the thread
 * you just left cannot attach themselves to the page you just opened.
 *
 * @module breadcrumbLeafActionsStore
 */

interface BreadcrumbLeafActionsState {
  readonly pathname: string | null;
  readonly actions: BreadcrumbLeafActions | null;
  readonly publish: (pathname: string, actions: BreadcrumbLeafActions) => void;
  readonly clear: (pathname: string) => void;
}

export const useBreadcrumbLeafActionsStore = create<BreadcrumbLeafActionsState>((set) => ({
  pathname: null,
  actions: null,
  publish: (pathname, actions) => set({ pathname, actions }),
  clear: (pathname) =>
    set((state) => (state.pathname === pathname ? { pathname: null, actions: null } : state)),
}));

/** Reads the published actions, ignoring anything left over from another route. */
export function selectPublishedLeafActions(
  state: Pick<BreadcrumbLeafActionsState, "pathname" | "actions">,
  pathname: string,
): BreadcrumbLeafActions | null {
  return state.pathname === pathname ? state.actions : null;
}
